import ConversationChannelAmazonIcon from "@assets/conversation/amazon/channel_amazon"
import ConversationCardEmailIcon from "@assets/conversation/email/card_email"
import ConversationCardFacebook from "@assets/conversation/facebook/card_facebook"
import ConversationCardHepsiburadaIcon from "@assets/conversation/hepsiburada/card_hepsiburada"
import ConversationCardInstagram from "@assets/conversation/instagram/card_instagram"
import ConversationCardLiveChatIcon from "@assets/conversation/live_chat/card_live_chat"
import ConversationCardN11Icon from "@assets/conversation/n11/card_n11"
import ConversationCardPazaramaIcon from "@assets/conversation/pazarama/card_pazarama"
import ConversationChannelPhoneIcon from "@assets/conversation/phone"
import ConversationCardTelegram from "@assets/conversation/telegram/card_telegram"
import ConversationCardTrendyolIcon from "@assets/conversation/trendyol/card_trendyol"
import ConversationCardWhatsappIcon from "@assets/conversation/whatsapp/card_whatsapp"
import ConversationCardWhatsappBusinessIcon from "@assets/conversation/whatsapp_business/card_whatsapp_business"
import { PRIMARY } from "@constants/colors"
import { useAiAnswersPageContext } from "@contexts/main/ai_answers_page_context"
import { listAiConversationsQuery } from "@domain/ai_answers"
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded"
import {
  Avatar,
  Badge,
  Box,
  Divider,
  IconButton,
  LinearProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material"
import AiAnswersFilterPopover from "@pages/main/ai_answers/sub_components/AiAnswersFilterPopover"
import { useInfiniteQuery } from "@tanstack/react-query"
import { extractConversationId } from "@utils/ai_conversation_id"
import { showErrorToast } from "@utils/toast"
import {
  AiMessages,
  AiMessageStatusEnum,
  ChannelType,
  PlatformType
} from "__generated__/graphql"
import dayjs from "dayjs"
import { useEffect, useMemo, useRef, useState } from "react"
import { runEventLabel } from "./aiMessage.utils"

interface Props {
  selectedItem: AiMessages | null
  onSelect: (item: AiMessages) => void
}

export default function AiMessageList({ selectedItem, onSelect }: Props) {
  const { filters, translate, resetFilters } = useAiAnswersPageContext()
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const sentinelRef = useRef<HTMLDivElement | null>(null)
  const debounceRef = useRef<number | null>(null)
  const onScrollCheck = useRef<() => void>(() => {})

  const customerId = filters.customerId
  const channelType = filters.channelType || undefined
  const status = filters.status || undefined
  const kind = filters.kind || undefined
  const [search, setSearch] = useState<string>("")
  const startDate = filters.startDate
  const endDate = filters.endDate

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isPending,
    isFetching,
    isFetchingNextPage
  } = useInfiniteQuery({
    queryKey: [
      "ai-message-list",
      search,
      customerId,
      channelType,
      status,
      startDate,
      endDate,
      kind
    ],
    queryFn: async ({ pageParam = 0 }) => {
      const searchedConversationId = extractConversationId(search)
      const response = await listAiConversationsQuery({
        customerId,
        channelType,
        status,
        kind,
        limit: 20,
        offset: pageParam,
        search: searchedConversationId ? undefined : search,
        conversationId: searchedConversationId,
        startDate,
        endDate
      })
      if (!response.isSuccess || !response.data) {
        showErrorToast(response.errorMessage ?? "Bir hata oluştu")
        return {
          docs: [],
          hasNextPage: false,
          totalDocs: 0
        }
      }
      return response.data
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.hasNextPage) return undefined
      const loaded = allPages.reduce((sum, p) => sum + (p.docs?.length ?? 0), 0)
      return loaded
    }
  })

  const items = useMemo<AiMessages[]>(() => {
    if (!data?.pages?.length) return []

    return data.pages.flatMap((p) =>
      (p.docs ?? [])
        .filter((m) => m?._id)
        .map((m) => ({
          _id: m?._id ?? "",
          customerId: m?.customerId ?? "",
          conversationId: m?.conversationId ?? null,
          answerId: m?.answerId ?? "",
          questionId: m?.questionId ?? "",
          question: m?.question ?? "",
          answer: m?.answer ?? "",
          status: m?.status ?? AiMessageStatusEnum.Failed,
          channelType: m?.channelType as ChannelType | null,
          error: m?.error ?? null,
          kind: m?.kind,
          outcome: m?.outcome,
          createdAt: m?.createdAt ?? new Date(),
          updatedAt: m?.updatedAt ?? new Date()
        }))
    )
  }, [data])

  useEffect(() => {
    if (!fetchNextPage || !hasNextPage) return
    const rootEl = scrollRef.current
    const el = sentinelRef.current
    if (!rootEl || !el) return

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          if (!isFetchingNextPage) fetchNextPage()
        }
      },
      { root: rootEl, rootMargin: "0px 0px 600px 0px", threshold: 0 }
    )
    io.observe(el)

    // --- Fallback: scroll altına yaklaşınca tetikle ---
    onScrollCheck.current = () => {
      const { scrollTop, clientHeight, scrollHeight } = rootEl
      const nearBottom = scrollTop + clientHeight >= scrollHeight - 600
      if (nearBottom && hasNextPage && !isFetchingNextPage) {
        fetchNextPage()
      }
    }
    rootEl.addEventListener("scroll", onScrollCheck.current, { passive: true })

    return () => {
      io.disconnect()
      rootEl.removeEventListener("scroll", onScrollCheck.current)
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  useEffect(() => {
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current)
    }
  }, [])

  const selectedConversationId = selectedItem?.conversationId?._id
  // Rows are one per conversation and a deep link resolves to whichever run the API returns, so
  // match on the conversation — the run id differs after a reload.
  const isSelected = (m: AiMessages) =>
    selectedConversationId
      ? m.conversationId?._id === selectedConversationId
      : m._id === selectedItem?._id

  const selectedRowRef = useRef<HTMLDivElement | null>(null)
  const hasSelectedRow = items.some(isSelected)
  useEffect(() => {
    selectedRowRef.current?.scrollIntoView({ block: "nearest" })
  }, [selectedConversationId, hasSelectedRow])

  const formatListTime = (d: Date) => {
    const t = dayjs(d)
    const now = dayjs()
    if (t.isSame(now, "day")) return t.format("HH:mm")
    if (t.isSame(now, "year")) return t.format("DD.MM HH:mm")
    return t.format("DD.MM.YYYY")
  }

  const ChannelMap = new Map<PlatformType, React.ReactNode>([
    [PlatformType.Email, <ConversationCardEmailIcon key={"email"} />],
    [PlatformType.Facebook, <ConversationCardFacebook key={"facebook"} />],
    [
      PlatformType.Hepsiburada,
      <ConversationCardHepsiburadaIcon key={"hepsiburada"} />
    ],
    [PlatformType.Instagram, <ConversationCardInstagram key={"instagram"} />],
    [PlatformType.LiveChat, <ConversationCardLiveChatIcon key={"livechat"} />],
    [PlatformType.N11, <ConversationCardN11Icon key={"n11"} />],
    [PlatformType.Telegram, <ConversationCardTelegram key={"telegram"} />],
    [PlatformType.Trendyol, <ConversationCardTrendyolIcon key={"trendyol"} />],
    [PlatformType.Pazarama, <ConversationCardPazaramaIcon key={"pazarama"} />],
    [
      PlatformType.WhatsAppBusiness,
      <ConversationCardWhatsappBusinessIcon key={"whatsapp-business"} />
    ],
    [
      PlatformType.WhatsAppWeb,
      <ConversationCardWhatsappIcon key={"whatsapp"} />
    ],
    [PlatformType.Amazon, <ConversationChannelAmazonIcon key={"amazon"} />],
    [
      PlatformType.Phone,
      <ConversationChannelPhoneIcon
        key={"phone"}
        size="10px"
        padding={0.8}
        fontSize="11px"
      />
    ]
  ])

  return (
    <Box
      sx={{
        width: 300,
        borderRight: 1,
        borderColor: "divider",
        height: "100%",
        display: "flex",
        flexDirection: "column"
      }}
      ref={scrollRef}
    >
      <Stack sx={{ p: 1 }} direction="row" alignItems="center" gap={0.5}>
        <AiAnswersFilterPopover />
        <Tooltip title={translate("refetch") || "Refetch"}>
          <IconButton
            size="small"
            onClick={() => {
              setSearch("")
              resetFilters()
            }}
            sx={{ flexShrink: 0 }}
          >
            <RefreshRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <TextField
          fullWidth
          size="small"
          placeholder={translate("search")}
          InputProps={{ sx: { borderRadius: 2 } }}
          onChange={(e) => {
            const v = e.target.value
            if (debounceRef.current) window.clearTimeout(debounceRef.current)
            debounceRef.current = window.setTimeout(() => {
              const s = v.trim()
              setSearch(s.length >= 3 ? s : "")
            }, 400)
          }}
        />
      </Stack>
      <Divider />
      <Box sx={{ flex: 1, overflow: "auto", position: "relative" }}>
        {(isLoading || isPending || isFetching || isFetchingNextPage) && (
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 2,
              bgcolor: "background.paper"
            }}
          >
            <LinearProgress />
          </Box>
        )}
        <List disablePadding>
          {items.map((m) => (
            <ListItemButton
              key={m._id}
              onClick={() => onSelect(m)}
              selected={isSelected(m)}
              ref={isSelected(m) ? selectedRowRef : undefined}
              sx={{
                alignItems: "flex-start",
                borderLeft: "3px solid transparent",
                "&.Mui-selected": {
                  borderLeftColor: PRIMARY,
                  bgcolor: `${PRIMARY}1f`
                },
                "&.Mui-selected:hover": {
                  bgcolor: `${PRIMARY}2e`
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 36, mt: 0.6, mr: 0.6 }}>
                <Box id="avatarArea">
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    badgeContent={ChannelMap.get(
                      String(
                        m.channelType ||
                          m.conversationId?.lastMessage?.channelType ||
                          m.conversationId?.channelType
                      ) as PlatformType
                    )}
                  >
                    <Avatar
                      sx={{
                        background: PRIMARY,
                        width: 32,
                        height: 32
                      }}
                      src={m.conversationId?.joinedPerson?.avatar}
                    />
                  </Badge>
                </Box>
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      justifyContent: "space-between"
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      noWrap
                      sx={{ minWidth: 0 }}
                      color={
                        m.status === AiMessageStatusEnum.Failed
                          ? "error"
                          : m.status === AiMessageStatusEnum.Running
                            ? "#f8d289"
                            : "inherit"
                      }
                      style={{
                        textDecoration:
                          m.conversationId && !m.conversationId.deleted
                            ? "none"
                            : "line-through"
                      }}
                    >
                      {m.conversationId && !m.conversationId.deleted
                        ? `${m.conversationId?.joinedPerson?.firstName} ${m.conversationId?.joinedPerson?.lastName}`
                        : "Silinen Sohbet"}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        flexShrink: 0,
                        whiteSpace: "nowrap",
                        fontVariantNumeric: "tabular-nums"
                      }}
                      title={dayjs(m.createdAt as Date).format(
                        "DD.MM.YYYY HH:mm:ss"
                      )}
                    >
                      {formatListTime(m.createdAt as Date)}
                    </Typography>
                  </Box>
                }
                secondary={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {m.kind ? runEventLabel(m, translate) : m.question}
                    </Typography>
                  </Box>
                }
              />
            </ListItemButton>
          ))}
          {hasNextPage && <div ref={sentinelRef} style={{ height: 1 }} />}
        </List>
      </Box>
    </Box>
  )
}
